/* PNOE Gift Card Bonus / staged cart sync.
   Load only with theme setting ON, after product and discount are verified.
   Shopify Buy X Get Y sets the price. Never invent a zero-price line.
*/
(() => {
  "use strict";
  const params = document.currentScript?.dataset || {};
  const buyId = Number(params.buyVariantId);
  const giftId = Number(params.bonusVariantId);
  if (!Number.isSafeInteger(buyId) || !Number.isSafeInteger(giftId) ||
      buyId <= 0 || giftId <= 0 || buyId === giftId) return;
  const root = (window.Shopify?.routes?.root || "/").replace(/\/?$/, "/");
  const propertyName = "_pnoe_gift_card_bonus";
  let busy = false, pending = false, timeoutId = 0;
  // Prevent repeated failed add/remove cycles that can trigger Shopify rate limits.
  let failedAttemptSignature = null;
  let attemptSignature = null;
  const signature = cart => JSON.stringify({
    products:(cart.items||[])
      .filter(item=>Number(item.variant_id)!==giftId)
      .map(item=>[item.variant_id,item.quantity,item.final_line_price])
      .sort((a,b)=>Number(a[0])-Number(b[0])),
    codes:(cart.discount_codes||[]).map(item=>[item.code,item.applicable]),
    currency:cart.currency
  });
  const cartUrl = root + "cart.js";
  const load = async () => {
    const response = await fetch(cartUrl, {credentials:"same-origin",cache:"no-store"});
    if (!response.ok) throw new Error("Could not read cart");
    return response.json();
  };
  const post = async (endpoint, body) => {
    const response = await fetch(root + endpoint, {
      method:"POST", credentials:"same-origin",
      headers:{"Content-Type":"application/json",Accept:"application/json"},
      body:JSON.stringify(body)
    });
    if (!response.ok) throw new Error("Could not update cart");
    return response.json();
  };
  const clearMessage = () => document.getElementById("PnoeBonusCartAlert")?.remove();
  function showMessage() {
    const container=document.querySelector("#Cart-Drawer [data-cart-drawer-content]") ||
      document.querySelector("#Cart-Page [data-cart-page-content]");
    if (!container) return;
    let alert=document.getElementById("PnoeBonusCartAlert");
    if (!alert) {
      alert=document.createElement("p");
      alert.id="PnoeBonusCartAlert";
      alert.className="pnoe-bf-bonus-warning";
      alert.setAttribute("role","status");
      container.prepend(alert);
    }
    const greek=/^el(?:-|$)/i.test(document.documentElement.lang);
    alert.textContent=greek
      ? "Δεν επιβεβαιώθηκε η δωρεάν Gift Card 20€. Έλεγξε ότι εμφανίζεται με 0,00€ και ότι το σύνολο είναι σωστό πριν ολοκληρώσεις την παραγγελία."
      : "The free €20 gift card could not be verified. Check that it shows €0.00 and that your total is correct before checkout.";
  }
  const getPromos = cart => (cart.items || []).filter(item =>
    Number(item.variant_id) === giftId && String(item.properties?.[propertyName]) === "1");
  const quantity = items => items.reduce((n,item)=>n+(Number(item.quantity)||0),0);
  async function refresh(cart) {
    const component = document.querySelector("cart-items");
    if (component?.getSectionsToRender && component?.renderSections) {
      const sections = [...new Set(component.getSectionsToRender().map(x=>x.section))];
      if (sections.length) {
        const response = await fetch(root + "?sections=" + encodeURIComponent(sections.join(",")), {
          credentials:"same-origin",cache:"no-store"
        });
        if (response.ok) component.renderSections(await response.json());
      }
    }
    document.dispatchEvent(new CustomEvent("cart:updated", {detail:{cart,source:"pnoe-gift-card-bonus"}}));
  }
  async function sync() {
    if (busy) {pending=true;return;}
    busy=true;
    try {
      clearMessage();
      const cart=await load();
      attemptSignature=signature(cart);
      const wanted=quantity((cart.items||[]).filter(item=>Number(item.variant_id)===buyId));
      if (wanted>0 && failedAttemptSignature===attemptSignature) return;
      if (failedAttemptSignature!==attemptSignature) failedAttemptSignature=null;
      const promos=getPromos(cart);
      const existing=quantity(promos);
      // Also validate existing gift-card lines: the cart can lose its
      // automatic BXGY discount after a code or quantity change.
      if (wanted === existing) {
        const charged = promos.filter(item=>Number(item.final_line_price)>0);
        if (charged.length) {
          const changes={};
          charged.forEach(item=>{changes[item.key]=0;});
          await post("cart/update.js",{updates:changes});
          failedAttemptSignature=attemptSignature;
          await refresh(await load());
          showMessage();
        }
        return;
      }
      if (wanted > existing) {
        await post("cart/add.js", {items:[{
          id:giftId,quantity:Math.min(100,wanted-existing),
          properties:{[propertyName]:"1"}
        }]});
      } else {
        const updates={};
        let keep=wanted;
        promos.forEach(item=>{
          const allowed=Math.min(Number(item.quantity)||0,keep);
          updates[item.key]=allowed;
          keep-=allowed;
        });
        if (Object.keys(updates).length) await post("cart/update.js",{updates});
      }
      let updated=await load();
      const unpaid=getPromos(updated).filter(item => Number(item.final_line_price)>0);
      const bonusCouldNotBeApplied=unpaid.length>0;
      if (unpaid.length) {
        const updates={};
        unpaid.forEach(item=>{updates[item.key]=0;});
        await post("cart/update.js",{updates});
        updated=await load();
        document.dispatchEvent(new CustomEvent("pnoe:gift-bonus-unavailable",{
          detail:{message:"Bonus not free; the paid bonus gift card was removed"}
        }));
      }
      if (bonusCouldNotBeApplied) failedAttemptSignature=attemptSignature;
      await refresh(updated);
      if (bonusCouldNotBeApplied) showMessage();
    } catch (error) {
      if (attemptSignature) failedAttemptSignature=attemptSignature;
      showMessage();
      console.warn("PNOE Gift Card Bonus sync:",error?.message||error);
    } finally {
      busy=false;
      if (pending) {pending=false;queue();}
    }
  }
  function queue(event) {
    if(event?.detail?.source==="pnoe-gift-card-bonus") return;
    clearTimeout(timeoutId);
    timeoutId=setTimeout(()=>{void sync();},280);
  }
  ["cart-update","cart:updated","cart:added","quantity-update","cart:refresh"].forEach(
    name=>document.addEventListener(name,queue)
  );
  document.addEventListener("click",event=>{
    if(event.target.closest("[data-add-to-cart]")){
      setTimeout(queue,850);
    }
  },{passive:true});
  if(document.readyState==="loading")document.addEventListener("DOMContentLoaded",queue,{once:true});
  else queue();
  addEventListener("pageshow",queue);
})();
